import type { ChatSession, GenerationTestHints } from '../types';
import { restoreStoredClarifyState } from '../utils/clarifyRetryState';
import { apiFetch } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || '';

export interface DeleteRemoteSessionResult {
  ok: boolean;
  cancelledRequestIds?: string[];
  cancelledQueueJobIds?: string[];
}

export async function loadRemoteSessions(): Promise<ChatSession[]> {
  const response = await apiFetch(`${API_BASE}/api/conversations/sessions`);

  if (!response.ok) {
    throw new Error('Failed to load conversation sessions');
  }

  const data = await response.json() as { sessions?: ChatSession[] };
  return Array.isArray(data.sessions) ? data.sessions : [];
}

/**
 * One conversation with its messages. `loadRemoteSessions` returns a summary
 * list with no message bodies, so this is how a conversation's content is
 * fetched when it is actually opened.
 */
export async function loadRemoteSession(sessionId: string): Promise<ChatSession | null> {
  const response = await apiFetch(
    `${API_BASE}/api/conversations/sessions/${encodeURIComponent(sessionId)}`,
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error('Failed to load conversation session');
  }

  const data = await response.json() as { session?: ChatSession };
  // The one place a conversation's messages arrive from the server.
  return data.session ? restoreStoredClarifyState(data.session) : null;
}

/**
 * A failed poll of the test-hints route, carrying the HTTP status so the
 * caller can tell a dead lookup (404/401/403 — this turn will never answer)
 * from a transient one (network error, 5xx, 429 — worth asking again).
 */
export class TestHintsRequestError extends Error {
  constructor(public readonly status: number) {
    super(`Failed to load test-case hints (HTTP ${status})`);
    this.name = 'TestHintsRequestError';
  }
}

/**
 * The model's reading of this turn's test cases (TA9c), which the turn never
 * waits for: it lands in the background, after delivery, and nothing pushes
 * that update into the already-delivered message on its own. Polled by
 * `useTestHints` while it is still pending.
 *
 * Keyed by `requestId`, never a message id (2026-09-29 review): the
 * browser's own assistant message id (`message.id`) is never the id the
 * server stores the row under, so a lookup keyed by it 404s every time —
 * see `readGenerationTestHints` in auth-core for the full story.
 */
export async function loadRemoteRequestTestHints(
  sessionId: string,
  requestId: string,
): Promise<GenerationTestHints | null> {
  const response = await apiFetch(
    `${API_BASE}/api/conversations/sessions/${encodeURIComponent(sessionId)}/requests/${encodeURIComponent(requestId)}/test-hints`,
  );

  if (!response.ok) {
    throw new TestHintsRequestError(response.status);
  }

  const data = await response.json() as { testHints?: GenerationTestHints | null };
  return data.testHints ?? null;
}

export async function saveRemoteSessions(sessions: ChatSession[]): Promise<void> {
  const response = await apiFetch(`${API_BASE}/api/conversations/sessions`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sessions }),
  });

  if (!response.ok) {
    throw new Error('Failed to save conversation sessions');
  }
}

export async function updateRemoteSession(
  sessionId: string,
  updates: { title?: string; status?: string; sessionConfig?: ChatSession['sessionConfig']; isStarred?: boolean },
): Promise<void> {
  const response = await apiFetch(`${API_BASE}/api/conversations/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error('Failed to update conversation session');
  }
}

export async function deleteRemoteSession(sessionId: string): Promise<DeleteRemoteSessionResult> {
  const response = await apiFetch(`${API_BASE}/api/conversations/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete conversation session');
  }

  const data = await response.json() as DeleteRemoteSessionResult;
  return data;
}
